/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef, booleanAttribute } from '@angular/core';
import { Document } from '../../store/models/document.model';
import { View, Workflow } from '../../store/models/workflow.model';
import { DynamicField } from '../fields/dynamic-field';
import { DocumentReader } from '../../store/models/document-reader';
import { DocumentWriter } from '../../store/models/document-writer';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil, tap, filter } from 'rxjs/operators';
import { WorkflowStep } from 'src/app/modules/core/store/models/workflow.model';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { Permissions } from 'src/app/modules/authentication/store/auth';
import { WorkflowTransitionService } from './workflow-transition.service';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ColorItemSelectComponent } from '../color-item-select/color-item-select.component';
import { WorkflowTransitionFormComponent } from '../../pages/workflow-transition/workflow-transition-form.component';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-workflow-transition-select2',
    imports: [CommonModule, ColorItemSelectComponent, WorkflowTransitionFormComponent],
    templateUrl: './workflow-transition-select2.component.html',
    styleUrls: ['./workflow-transition-select2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowTransitionSelectComponent2 implements OnInit, OnDestroy {
  @Input() set document(value: Document) {
    this.document$.next(value);
  }
  document$: BehaviorSubject<Document> = new BehaviorSubject(null);

  @Input() set isEdit(value: boolean) {
    this.isEdit$.next(value);
  }
  isEdit$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Input() set dataset(dataset: Dataset) {
    this.dataset$.next(dataset);
  }
  dataset$: BehaviorSubject<Dataset> = new BehaviorSubject(null);

  @Input() set hideSingleWorkflow(value: boolean) {
    this.hideSingleWorkflow$.next(value);
  }
  hideSingleWorkflow$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Input({ transform: booleanAttribute }) noMargin = false;

  destroy$: Subject<void> = new Subject();
  workflow: Workflow;
  selectedStepUId: string = null;
  selectedStatusName: string = null;
  selectedStatusColorCode: string = null;
  workflowStepListItems: WorkflowStep[] = [];
  DatasetType = DatasetType;
  DatasetSubtype = DatasetSubtype;
  readOnlyUser: boolean;
  canUpdateDocuments: boolean;
  showFieldAsEdit: boolean = false;
  showTransitionForm: boolean = false;
  selectedNextWorkflowStep: WorkflowStep;
  selectedPreviousWorkflowStep: WorkflowStep;
  workflowTransitionView: View;
  hideControl: boolean = false;

  constructor(
    private store: Store,
    private changeDetector: ChangeDetectorRef,
    private workflowTransitionService: WorkflowTransitionService,
    private translateService: TranslateService,
    private notify: SystemNotificationsService
  ) {}

  ngOnInit(): void {
    combineLatest([this.dataset$, this.document$, this.isEdit$, this.hideSingleWorkflow$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([dataset, document]) => !!dataset && !!document),
        tap(([dataset, document, isEdit, hideSingleWorkflow]) => {
          this.populateWorkflowStepListItems(document, dataset.workflow);

          this.populateSelectedWorkflowStepValues(document, dataset.workflow);

          this.calculateShouldDisplayFieldForEdit(isEdit, dataset);

          this.calculateShouldHideFieldForSingleStep(hideSingleWorkflow);
        })
      )
      .subscribe();

    this.workflowTransitionService.transitionWorkflowWithForm$
      .pipe(
        takeUntil(this.destroy$),
        tap(e => {
          this.workflowTransitionView = this.dataset$.value.workflow.views.find(v => v.uId === e.transitionViewUId);

          this.showTransitionForm = true;
        })
      )
      .subscribe();

    this.workflowTransitionService.transitionCancelled$
      .pipe(
        takeUntil(this.destroy$),
        tap(e => {
          if (this.document$.value.uid === e.document.uid) {
            this.selectedStepUId = e.previousStep.uId;
            this.selectedStatusName = e.previousStep.statusName;
            this.selectedStatusColorCode = e.previousStep.statusHtmlColorCode;
            this.selectedNextWorkflowStep = null;
            this.selectedPreviousWorkflowStep = null;
            this.workflowTransitionView = null;

            this.showTransitionForm = false;

            this.changeDetector.markForCheck();
          }
        })
      )
      .subscribe();

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.readOnlyUser = userContext?.isReadOnlyToken;
        this.canUpdateDocuments = userContext?.hasPermission(Permissions.DocumentUpdate);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.document$.complete();
    this.dataset$.complete();
    this.isEdit$.complete();
    this.hideSingleWorkflow$.complete();
  }

  calculateShouldDisplayFieldForEdit(isEdit: boolean, dataset: Dataset) {
    this.showFieldAsEdit = !isEdit && this.workflowStepListItems?.length > 0 && !dataset?.isSubscribedRecord;
  }

  calculateShouldHideFieldForSingleStep(hideSingleWorkflow: boolean) {
    this.hideControl = this.workflowStepListItems?.length === 0 && hideSingleWorkflow;
  }

  populateSelectedWorkflowStepValues(document: Document, workflow: Workflow) {
    const step = workflow.workflowSteps.find(wfs => wfs.uId === document.workflow_step_uid);
    if (!!!step) {
      return;
    }

    this.selectedStepUId = step.uId;
    this.selectedStatusName = step.statusName;
    this.selectedStatusColorCode = step.statusHtmlColorCode;
  }

  populateWorkflowStepListItems(document: Document, workflow: Workflow) {
    if (!document || !workflow) {
      return [];
    }

    this.workflow = workflow;

    const listItems: WorkflowStep[] = [];

    const reader = new DocumentReader(document);

    const step = workflow?.workflowSteps?.find(ws => ws.uId === reader.workflowStepUId);
    if (!step) {
      return [];
    }

    const actions = step.actions.filter(a => !!a.destinationStepUId).sort((s1, s2) => s1.sortOrder - s2.sortOrder);

    actions.map(a => {
      listItems.push(workflow.workflowSteps.find(ws => ws.uId === a.destinationStepUId));
    });

    this.workflowStepListItems = listItems;
  }

  stepSelected(workflowStep: WorkflowStep, isCancel?: boolean): void {
    const currWfStep = this.workflow.workflowSteps.find(ws => ws.uId === this.selectedStepUId);
    const nextWfStepAction = currWfStep?.actions?.find(a => a.destinationStepUId === workflowStep.uId);
    const nextWfStep = workflowStep;

    // set the control display values
    this.selectedStepUId = workflowStep.uId;
    this.selectedStatusName = workflowStep.statusName;
    this.selectedStatusColorCode = workflowStep.statusHtmlColorCode;
    this.selectedNextWorkflowStep = nextWfStep;
    this.selectedPreviousWorkflowStep = currWfStep;

    if (!!isCancel) {
      return;
    }

    // we are either going to immediately transition to the new step
    // or we are going to show a trasition form. The result of the form
    // will either be a successful transition or a cancellation of the request.
    if (!!nextWfStepAction.transitionViewUId) {
      this.workflowTransitionService.showFormAndTransitionWorkflow({
        dataset: this.dataset$.value,
        document: this.document$.value,
        previousStep: currWfStep,
        nextStep: nextWfStepAction,
        transitionViewUId: nextWfStepAction.transitionViewUId
      });
    } else {
      this.workflowTransitionService.transitionWorkflow({
        dataset: this.dataset$.value,
        document: this.document$.value,
        previousStep: currWfStep,
        nextStep: nextWfStep
      });
    }
  }

  onCloseTransitionForm(): void {
    this.stepSelected(this.selectedPreviousWorkflowStep, true);
    this.showTransitionForm = false;
  }

  onSaveTransitionForm(controls: DynamicField[]): void {
    const writer = new DocumentWriter(this.document$.value);

    for (const control of controls.filter(c => !!c.field)) {
      const isEmpty = control?.value == null || control?.value === '';
      if (control?.viewComponent?.configuration?.isRequired && isEmpty) {
        this.translateService.get('MANAGEMENT.WORKFLOW.FIELD-REQUIRED').subscribe(value => {
          this.notify.error(control.field.displayName + ' ' + value);
        });

        return;
      }
      writer.setFieldValue(control.field.systemName, control.value);
    }

    writer.workflowStepUId = this.selectedStepUId;

    this.showTransitionForm = false;

    this.workflowTransitionService.transitionWorkflow({
      dataset: this.dataset$.value,
      document: writer.document,
      previousStep: this.selectedPreviousWorkflowStep,
      nextStep: this.selectedNextWorkflowStep
    });
  }
}
